import { MemberSearchQuery_members } from '@frontend/app/queries/types/MemberSearchQuery';
import { ISocialAccount } from '@components';
import type {
  DemographicsDataAdapter,
  DemographicsData,
  DemographicsSummary,
  DemographicsDisplayConfig,
  AgeDistributionEntry,
  GenderDistributionEntry,
  LocationEntry,
} from '@/types/demographics';

type MemberWithSocialData = {
  member: MemberSearchQuery_members | null;
  socialData?: ISocialAccount | null;
};

export class MemberDemographicsAdapter implements DemographicsDataAdapter<MemberWithSocialData> {
  extractDemographicsData(source: MemberWithSocialData): DemographicsData | null {
    const { socialData } = source;

    if (!socialData?.demographics_report) {
      return null;
    }

    // Extract age distribution from values array
    const ageDistribution: AgeDistributionEntry[] = socialData.demographics_report.age?.[0]?.values?.map((valueEntry) => ({
      ageBucket: this.formatAgeRange(valueEntry.dimension_values[0]),
      percentage: valueEntry.percentage * 100, // Convert decimal to percentage
    })).sort((a, b) => b.percentage - a.percentage) || [];

    // Extract gender distribution from values array
    const genderDistribution: GenderDistributionEntry[] = socialData.demographics_report.gender?.[0]?.values?.map((valueEntry) => ({
      gender: this.formatGender(valueEntry.dimension_values[0]),
      percentage: valueEntry.percentage * 100, // Convert decimal to percentage
    })).sort((a, b) => b.percentage - a.percentage) || [];

    // Extract location data from values array
    const topCountries: LocationEntry[] = socialData.demographics_report.country?.[0]?.values?.slice(0, 5).map((valueEntry) => ({
      name: valueEntry.dimension_values[0],
      percentage: valueEntry.percentage * 100, // Convert decimal to percentage
    })).sort((a, b) => b.percentage - a.percentage) || [];

    const topCities: LocationEntry[] = socialData.demographics_report.city?.[0]?.values?.slice(0, 5).map((valueEntry) => ({
      name: valueEntry.dimension_values[0],
      percentage: valueEntry.percentage * 100, // Convert decimal to percentage
    })).sort((a, b) => b.percentage - a.percentage) || [];

    return {
      ageDistribution,
      genderDistribution,
      topCountries,
      topCities,
    };
  }

    extractSummary(source: MemberWithSocialData): DemographicsSummary {
    const { socialData } = source;

    if (!socialData?.demographics_report) {
      return {
        ageBucket: '--',
        gender: '--',
        country: '--',
        city: '--',
      };
    }

    // Get the most common age bucket (highest percentage from values array)
    const ageValues = socialData.demographics_report.age?.[0]?.values || [];
    const topAgeValue = ageValues.sort((a, b) => b.percentage - a.percentage)[0];
    const ageBucket = topAgeValue ? this.formatAgeRange(topAgeValue.dimension_values[0]) : '--';

    // Get the most common gender (highest percentage from values array)
    const genderValues = socialData.demographics_report.gender?.[0]?.values || [];
    const topGenderValue = genderValues.sort((a, b) => b.percentage - a.percentage)[0];
    const gender = topGenderValue ? this.formatGender(topGenderValue.dimension_values[0]) : '--';

    // Get the most common country (highest percentage from values array)
    const countryValues = socialData.demographics_report.country?.[0]?.values || [];
    const topCountryValue = countryValues.sort((a, b) => b.percentage - a.percentage)[0];
    const country = topCountryValue ? topCountryValue.dimension_values[0] : '--';

    // Get the most common city (highest percentage from values array)
    const cityValues = socialData.demographics_report.city?.[0]?.values || [];
    const topCityValue = cityValues.sort((a, b) => b.percentage - a.percentage)[0];
    const city = topCityValue ? topCityValue.dimension_values[0] : '--';

    return {
      ageBucket,
      gender,
      country,
      city,
    };
  }

  getDisplayConfig(): DemographicsDisplayConfig {
    return {
      ageBuckets: {
        '13-17': { displayName: '13-17', color: '#8884d8' },
        '18-24': { displayName: '18-24', color: '#82ca9d' },
        '25-34': { displayName: '25-34', color: '#ffc658' },
        '35-44': { displayName: '35-44', color: '#ff7c7c' },
        '45-54': { displayName: '45-54', color: '#8dd1e1' },
        '55-64': { displayName: '55-64', color: '#d084d0' },
        '65+': { displayName: '65+', color: '#ffb347' },
      },
      genders: {
        'Male': { displayName: 'Male', color: '#8884d8' },
        'Female': { displayName: 'Female', color: '#82ca9d' },
        'Other': { displayName: 'Other', color: '#ffc658' },
      },
      unknownValues: {
        ageBucket: { displayName: 'Unknown', color: '#cccccc' },
        gender: { displayName: 'Unknown', color: '#cccccc' },
      },
    };
  }

  private formatAgeRange(key: string): string {
    if (!key) {
      return '--';
    }
    const ageMap: Record<string, string> = {
      'age_13_17': '13-17',
      'age_18_24': '18-24',
      'age_25_34': '25-34',
      'age_35_44': '35-44',
      'age_45_54': '45-54',
      'age_55_64': '55-64',
      'age_65_plus': '65+',
    };
    return ageMap[key.toLowerCase()] || key;
  }

  private formatGender(key: string): string {
    if (!key) {
      return '--';
    }
    const genderMap: Record<string, string> = {
      'male': 'Male',
      'female': 'Female',
      'custom': 'Other',
    };
    return genderMap[key.toLowerCase()] || key;
  }
}
